import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Drawer, FormControlLabel, Grid2, ListSubheader, MenuItem, Switch, Tab, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { SelectValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { FormFieldKeyConditionEnum } from "../../../_common/enum/forms/fomFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../../../_common/enum/forms/fomFieldKeyConditionType.enum";
import { FormFieldKeyActionEnum } from "../../../_common/enum/forms/formFieldKeyAction.enum";
import { FormFieldValueTypeEnum, isFormFieldValueTypeEnumOptions } from "../../../_common/enum/forms/formFieldValueType.enum";
import { IFormFieldKeyManage } from "../../../_common/models/formFieldKey";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { FormFieldKeyLookupMultipleAutoComplete } from "../../../_components/autocomplete/formFieldKeyLookup.multipleComplete";
import { StandardButton } from "../../../_components/button/primary.button";
import { DrawerHeader } from "../../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { formFieldKeyService } from "../../../_services/formFieldKey.service";
import { formFieldKeyLookupService } from "../../../_services/formFieldKeyLookup.service";

export function FormFieldKeyManageDrawer(params: {
    emitOnSave: () => void,
    emitOnClose: () => void
}) {
    let navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isInFormTemplateSectionView = location.pathname.toLowerCase().includes('formtemplatesection') &&
        !location.pathname.toLowerCase().includes('formtemplate/');

    const { formTemplateSectionId, formFieldKeyId } = useParams<{ formTemplateSectionId: string, formFieldKeyId: string }>();
    const _formTemplateSectionId = +(formTemplateSectionId || 0);
    const _formFieldKeyId = +(formFieldKeyId || 0);

    const tableId = searchParams.get('tableId');
    const rowNumber = searchParams.get('row');
    const columnNumber = searchParams.get('col');

    const [data, setData] = useState(getResetData);
    const [isLoading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('1');
    const [sectionFormFieldKeyLookups, setSectionFormFieldKeyLookups] = React.useState([]);
    const [sectionFormFieldKeys, setSectionFormFieldKeys] = React.useState([]);

    const formFieldValueTypeEnum = Object.keys(FormFieldValueTypeEnum);

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTab(newValue.toString());
    };

    useEffect(() => {
        const parsedId = Number(formFieldKeyId);
        const hasRouteKey = formFieldKeyId !== undefined && formFieldKeyId !== null && String(formFieldKeyId).length > 0;
        if (hasRouteKey) {
            if (!Number.isFinite(parsedId) || parsedId <= 0) {
                setData(getResetData());
                return;
            }
            fetchData();
        } else {
            const resetData = getResetData();
            setData(resetData);
        }
    }, [formFieldKeyId]);

    useEffect(() => {
        if (!_formFieldKeyId && tableId) {
            setData((_data: any) => ({
                ..._data,
                subSectionId: +(tableId),
                subSectionTypeId: FormFieldValueTypeEnum.Table,
                rowNumber: rowNumber ? +(rowNumber) : _data.rowNumber,
                columnNumber: columnNumber ? +(columnNumber) : _data.columnNumber
            }));
        }
    }, [tableId, rowNumber, columnNumber]);


    useEffect(() => {
        if (data.hasFormFieldConditionDependency === true && (!data.formFieldKeyConditionDependencies || !data.formFieldKeyConditionDependencies.length)) {
            setData((_data: any) => ({ ..._data, formFieldKeyConditionDependencies: [{ formFieldKeyId: 0, conditionId: 0, value: "" }] }))
        }
    }, [data.hasFormFieldConditionDependency]);

    useEffect(() => {
        if (data.hasFormFieldKeyDependency === true && sectionFormFieldKeys.length < 1) {
            fetchFormFieldKeyData();
        }
        else if (data.hasFormFieldKeyDependency === false) {
            setData((_data: any) => { return { ..._data, formFieldKeyDependencyId: null } });
        }

    }, [data.hasFormFieldKeyDependency]);

    function getResetData(): IFormFieldKeyManage {
        const resetData: any = {
            id: 0 || +(_formFieldKeyId || 0),
            label: "",
            shortName: "",
            showLabel: false,
            formFieldValueTypeId: "",
            formTemplateSectionId: _formTemplateSectionId,
            subSectionId: tableId ? +(tableId) : undefined,
            rowNumber: rowNumber ? +(rowNumber) : undefined,
            columnNumber: columnNumber ? +(columnNumber) : undefined,
            hasFormFieldKeyLookupDependency: false,
            hasFormFieldKeyDependency: false,
            hasSelectCondition: false,
            formFieldKeyLookupDependencyId: null,
            formFieldKeyDependencyId: null,
            isGlobal: false,
            isRequired: false,
            isAdminField: false,
            xsWidth: "",
            mdWidth: "",
            formFieldKeyFormFieldKeyLookups: [],
            formFieldKeyConditionDependencies: [],
            formFieldKeyActionId: "",
            description: "",
        };

        if (tableId) {
            resetData.subSectionTypeId = FormFieldValueTypeEnum.Table;
        }

        return resetData;
    }

    function normalizeLoadedFormFieldKey(raw: any) {
        if (!raw || typeof raw !== "object") return raw;
        const formFieldValueTypeId =
            raw.formFieldValueTypeId ?? raw.FormFieldValueTypeId;
        const label = raw.label ?? raw.Label ?? "";
        const shortName = raw.shortName ?? raw.ShortName ?? "";
        const id = raw.id ?? raw.Id;
        let coercedType = formFieldValueTypeId;
        if (typeof coercedType === "string" && /^\d+$/.test(coercedType)) {
            coercedType = Number(coercedType);
        }
        return {
            ...raw,
            id,
            label,
            shortName,
            formFieldValueTypeId: coercedType,
        };
    }

    function fetchData() {
        setLoading(true);
        return formFieldKeyService
            .get(+(_formFieldKeyId || 0))
            .then(
                (json) => {
                    if (json) {
                        const merged = normalizeLoadedFormFieldKey(json);
                        merged.formFieldValueTypeIdDb = merged.formFieldValueTypeId ?? null;

                        if (merged.formFieldKeyFormFieldKeyLookups) {
                            merged.formFieldKeyFormFieldKeyLookups = merged.formFieldKeyFormFieldKeyLookups.map((m: any) => ({
                                id: m.formFieldKeyLookupId,
                            }))
                        }
                        setData(merged);
                    }
                    else setData(getResetData);
                }, handleErrorResponse
            )
            .finally(() => setLoading(false));
    }

    function fetchFormFieldKeyLookupData() {

        return formFieldKeyLookupService
            .dropdown(_formTemplateSectionId)
            .then(
                (json) => {
                    setSectionFormFieldKeyLookups(json);
                }, handleErrorResponse
            );
    }

    function fetchFormFieldKeyData() {
        return formFieldKeyService
            .dropdown(_formTemplateSectionId)
            .then(
                (json) => {
                    setSectionFormFieldKeys(json);
                }, handleErrorResponse
            );
    }

    function closeModal() {
        params.emitOnClose();
    }

    function save(e: any) {
        e.preventDefault();
        setSaving(true);

        var fileInputField = document.getElementById("image")
        const formData = new FormData();
        formData.append("id", (data.id || 0).toString());
        formData.append("file", (fileInputField as any)?.files[0]);
        formData.append("formTemplateSectionId", (data.formTemplateSectionId || 0).toString());
        formData.append("formFieldValueTypeId", (data.formFieldValueTypeId || 0).toString());
        formData.append("shortName", (data.shortName || "").toString());


        formData.append("xsWidth", (data.xsWidth || "").toString());
        formData.append("mdWidth", (data.mdWidth || "").toString());

        formData.append("rowNumber", (data.rowNumber || "").toString());
        formData.append("columnNumber", (data.columnNumber || "").toString());

        formData.append("minValue", (data.minValue || "").toString());
        formData.append("maxValue", (data.maxValue || "").toString());

        formData.append("label", (data.label || "").toString());
        formData.append("isRequired", (data.isRequired || false).toString());
        formData.append("showLabel", (data.showLabel || false).toString());
        formData.append("hasSelectCondition", (data.hasSelectCondition || false).toString());
        formData.append("formFieldActionId", (data.formFieldActionId || "").toString());
        formData.append("fileUploadTypeId", (data.fileUploadTypeId || "").toString());

        formData.append("description", (data.description || "").toString());
        formData.append("sectionOrder", (data.sectionOrder || "").toString());
        formData.append("subSectionId", (data.subSectionId || "").toString());
        formData.append("hasFormFieldConditionDependency", (data.hasFormFieldConditionDependency || false).toString());
        formData.append("isGlobal", (data.isGlobal || false).toString());
        formData.append("isAdminField", (data.isAdminField || false).toString());


        // formData.append("existingTableName", (data.sectionOrder || "").toString());
        // formData.append("condition", (data.sectionOrder || "").toString());

        formData.append("formFieldKeyActionId", (data.formFieldKeyActionId || "").toString());

        formData.append("hasFormFieldKeyLookupDependency", (data.hasFormFieldKeyLookupDependency || false).toString());
        formData.append("formFieldKeyLookupDependencyId", (data.formFieldKeyLookupDependencyId || "").toString());

        formData.append("hasFormFieldKeyDependency", (data.hasFormFieldKeyDependency || false).toString());
        formData.append("formFieldKeyDependencyId", (data.formFieldKeyDependencyId || "").toString());

        formData.append("dataType", (data.dataType || "").toString());

        formData.append("formFieldKeyConditionTypeId", (data.formFieldKeyConditionTypeId || "").toString());
        formData.append("isQuestion", (data.isQuestion || false).toString());
        formData.append("formFieldKeySpecialTypeId", (data.formFieldKeySpecialTypeId || "").toString());

        if (data.formFieldKeyFormFieldKeyLookups && data.formFieldKeyFormFieldKeyLookups.length) {
            data.formFieldKeyFormFieldKeyLookups.forEach((f: any, index: number) => {
                formData.append(`formFieldKeyFormFieldKeyLookups[${index}].formFieldKeyLookupId`, f.id);
            })
        }

        if (data.formFieldKeyConditionDependencies && data.formFieldKeyConditionDependencies.length && data.hasFormFieldConditionDependency) {
            data.formFieldKeyConditionDependencies.forEach((f: any, index: number) => {
                formData.append(`formFieldKeyConditionDependencies[${index}].Id`, f.id || 0);
                formData.append(`formFieldKeyConditionDependencies[${index}].FormFieldKeyId`, _formFieldKeyId.toString());
                formData.append(`formFieldKeyConditionDependencies[${index}].ConditionFormFieldKeyId`, f.conditionFormFieldKeyId || "");
                formData.append(`formFieldKeyConditionDependencies[${index}].Value`, f.value || "");
                formData.append(`formFieldKeyConditionDependencies[${index}].FormFieldKeyConditionId`, f.formFieldKeyConditionId || "");
            })
        }

        var fileInputField = document.getElementById("image")
        formData.append("file", (fileInputField as any)?.files[0]);

        return formFieldKeyService[(data.id || 0) > 0 ? "put" : "post"](formData)
            .then(
                (json) => {
                    toastr.success("Saved successfully");
                    params.emitOnSave();

                    if (+(_formFieldKeyId || 0) == 0) {
                        setData(getResetData);
                        navigate("../EditFormFieldKey/" + json.data)
                    }
                    else {
                        let _data = { ...data };
                        _data.formFieldValueTypeIdDb = _data.formFieldValueTypeId;
                        setData(_data);
                    }
                }, handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function disable() {
        setSaving(true);
        return formFieldKeyService
            .disable(+(_formFieldKeyId || 0))
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.emitOnSave();
                    closeModal();
                }, handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function handleChangeSwitch(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: !__data[event.target.name] } });
    };

    function handleChange(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: event.target.value } });
    };

    function handleSubSectionChange(event: any, target: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: event.target.value, subSectionTypeId: target?.formFieldValueTypeId ?? "" } });
    };

    function onError(event: any) {
        toastr.error("Please verify that all fields have correct input");
    }

    function getLookupValues() {
        var _returnItems: any[] = [];

        if (sectionFormFieldKeyLookups && sectionFormFieldKeyLookups.length > 0) {
            sectionFormFieldKeyLookups.map((formFieldKeyLookups: any) => {
                _returnItems.push(<ListSubheader key={"lookup_header_key_" + formFieldKeyLookups[0].id}>{formFieldKeyLookups[0].formFieldKeyShortName}</ListSubheader>);

                if (formFieldKeyLookups && formFieldKeyLookups.length > 0) {
                    formFieldKeyLookups.map((formFieldKeyLookup: any) => {
                        _returnItems.push(<MenuItem key={"lookup_key_" + formFieldKeyLookup.id} value={formFieldKeyLookup.id.toString()}>{formFieldKeyLookup.name}</MenuItem>);
                    })
                }
            })
        }

        return _returnItems;
    }

    const shouldShowValues = data.formFieldValueTypeIdDb == FormFieldValueTypeEnum.Radio ||
        data.formFieldValueTypeIdDb == FormFieldValueTypeEnum.Checkbox ||
        data.formFieldValueTypeIdDb == FormFieldValueTypeEnum.Dropdown ||
        data.formFieldValueTypeIdDb == FormFieldValueTypeEnum.List ||
        data.formFieldValueTypeIdDb == FormFieldValueTypeEnum.RadioButton;


    return (
        <Drawer
            anchor={"right"}
            variant={isInFormTemplateSectionView ? "persistent" : "temporary"}
            open={true}
            ModalProps={isInFormTemplateSectionView ? {
                keepMounted: true,
                hideBackdrop: true
            } : undefined}
            sx={isInFormTemplateSectionView ? {
                '& .MuiBackdrop-root': {
                    display: 'none'
                }
            } : undefined}>


            <DrawerHeader name={(!!_formFieldKeyId) ? "Update Form Field Key" : "Create Form Field Key"} onClose={closeModal} />

            <TabContext value={tab}>
                <Grid2 size={{ xs: 12 }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleTabChange}>
                        <Tab label="Field" value="1" />
                        <Tab label="Values" disabled={!(shouldShowValues)} value="2" />
                    </TabList>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <TabPanel value="1" sx={{ p: 0.5, m: 0.5 }}>
                        <ValidatorForm onError={onError} onSubmit={save} noValidate>
                            <Grid2 container spacing={1}>

                                <Grid2 size={{ xs: 12 }}>
                                    <EnumSelect
                                        key={`formFieldValueTypeId-${data.id}-${String(data.formFieldValueTypeId)}`}
                                        enumObject={FormFieldValueTypeEnum}
                                        label="Value Type"
                                        name="formFieldValueTypeId"
                                        value={
                                            data.formFieldValueTypeId === "" || data.formFieldValueTypeId == null
                                                ? ""
                                                : (() => {
                                                    const n = Number(data.formFieldValueTypeId);
                                                    return Number.isFinite(n) ? n : "";
                                                })()
                                        }
                                        onChange={handleChange}
                                        required />
                                </Grid2>

                                {(data.formFieldValueTypeId == FormFieldValueTypeEnum.DataTypeAutoComplete || data.formFieldValueTypeId == FormFieldValueTypeEnum.DataTypeMultipleAutoComplete ) &&
                                    <>
                                        <Grid2 size={{ xs: 12 }}>
                                            <EnumSelect
                                                key="dataType"
                                                enumObject={DataTypeEnum}
                                                label="Data Type"
                                                name="dataType"
                                                value={data.dataType}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                            />
                                        </Grid2>

                                    </>
                                }

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        label="Label"
                                        value={data.label}
                                        autoFocus
                                        name="label"
                                        required={data.formFieldValueTypeId != FormFieldValueTypeEnum.SubSection}
                                        fullWidth
                                        validators={data.formFieldValueTypeId != FormFieldValueTypeEnum.SubSection ? ['required'] : []}
                                        errorMessages={data.formFieldValueTypeId != FormFieldValueTypeEnum.SubSection ? ['This field is required'] : []}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                {(data.formFieldValueTypeId == FormFieldValueTypeEnum.List || data.formFieldValueTypeId == FormFieldValueTypeEnum.RadioButton) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={<Switch checked={data.showLabel} onChange={handleChangeSwitch} name="showLabel" />}
                                            label="Show Label"
                                        />
                                    </Grid2>
                                }

                                {data.formFieldValueTypeId == FormFieldValueTypeEnum.Slider &&
                                    <>
                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Min Value"
                                                value={data.minValue}
                                                name="minValue"
                                                fullWidth
                                                required
                                                type="number"
                                                validators={['required']}
                                                errorMessages={['This field is required']}
                                                onChange={handleChange}

                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Max Value"
                                                value={data.maxValue}
                                                name="maxValue"
                                                fullWidth
                                                required
                                                type="number"
                                                validators={['required']}
                                                errorMessages={['This field is required']}
                                                onChange={handleChange}

                                            />
                                        </Grid2>
                                    </>
                                }

                                {data.formFieldValueTypeId == FormFieldValueTypeEnum.FileUpload &&
                                    <Grid2 size={{ xs: 12 }}>

                                        <DataTypeAutoComplete
                                            dataType={DataTypeEnum.FileUploadType}
                                            label="File Upload Type"
                                            value={data.fileUploadTypeId}
                                            name={"fileUploadTypeId"}
                                            onChange={handleChange}
                                            required />

                                    </Grid2>

                                }

                                {data.formFieldValueTypeId != FormFieldValueTypeEnum.SubSection &&
                                    <Grid2 size={{ xs: 12 }}>

                                        <DataTypeAutoComplete
                                            dataType={DataTypeEnum.SubSection}
                                            parentId={_formTemplateSectionId}
                                            hasParent
                                            label="Sub Section"
                                            value={data.subSectionId}
                                            name={"subSectionId"}
                                            onChange={handleSubSectionChange} />

                                    </Grid2>
                                }

                                {(data.subSectionTypeId == FormFieldValueTypeEnum.Table || data.formFieldValueTypeId == FormFieldValueTypeEnum.Table) &&
                                    <>
                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Row Number"
                                                value={data.rowNumber}
                                                name="rowNumber"
                                                fullWidth
                                                type="number"
                                                onChange={handleChange}

                                            />
                                        </Grid2>

                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Column Number"
                                                value={data.columnNumber}
                                                name="columnNumber"
                                                type="number"
                                                fullWidth
                                                onChange={handleChange}

                                            />
                                        </Grid2>
                                    </>
                                }
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        label="Short Name"
                                        value={data.shortName}
                                        name="shortName"
                                        fullWidth
                                        helperText="TAG identifier field for documents and special functions. Must have no spaces or special characters"
                                        onChange={handleChange}

                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        label="Description"
                                        value={data.description}
                                        name="description"
                                        helperText="Description that will appear under the input box just like this one"
                                        fullWidth
                                        onChange={handleChange}

                                    />
                                </Grid2>

                                {(data.formFieldValueTypeId == FormFieldValueTypeEnum.Image || data.formFieldValueTypeId == FormFieldValueTypeEnum.FileDownload) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            label="File"
                                            type="file"
                                            id="image"
                                            fullWidth

                                            required
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </Grid2>
                                }

                                <Grid2 size={{ xs: 12 }}>
                                    <FormControlLabel
                                        control={<Switch checked={data.isRequired} onChange={handleChangeSwitch} name="isRequired" />}
                                        label="Is Field Required"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <FormControlLabel
                                        control={<Switch checked={data.isGlobal} onChange={handleChangeSwitch} name="isGlobal" />}
                                        label="Is Global Field"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <FormControlLabel
                                        control={<Switch checked={data.isAdminField} onChange={handleChangeSwitch} name="isAdminField" />}
                                        label="Is Admin Field"
                                    />
                                </Grid2>

                                {(isFormFieldValueTypeEnumOptions(data.formFieldValueTypeId)) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={<Switch checked={data.isQuestion} onChange={handleChangeSwitch} name="isQuestion" />}
                                            label="Is a Questionnaire Field"
                                        />
                                    </Grid2>
                                }

                                {(data.formFieldValueTypeId != FormFieldValueTypeEnum.Table) &&
                                    <>
                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Mobile Width"
                                                value={data.xsWidth}
                                                name="xsWidth"
                                                fullWidth
                                                type="number"
                                                validators={['matchRegexp:^([0-9]|1[0-2])$']}
                                                errorMessages={['TNumber must be between 0-12']}
                                                onChange={handleChange}

                                            />
                                        </Grid2>

                                        <Grid2 size={{ xs: 6 }}>
                                            <StandardTextValidator
                                                label="Normal Width"
                                                value={data.mdWidth}
                                                name="mdWidth"
                                                type="number"
                                                validators={['matchRegexp:^([0-9]|1[0-2])$']}
                                                errorMessages={['Number must be between 0-12']}
                                                fullWidth
                                                onChange={handleChange}

                                            />
                                        </Grid2>
                                    </>
                                }

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        label="Section Order"
                                        value={data.sectionOrder}
                                        name="sectionOrder"
                                        type="number"
                                        fullWidth
                                        onChange={handleChange}

                                        InputLabelProps={{
                                            shrink: !!data.sectionOrder,
                                        }}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <EnumSelect
                                        enumObject={FormFieldKeyActionEnum}
                                        label="Select Field Action"
                                        name="formFieldKeyActionId"
                                        value={data.formFieldKeyActionId}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                {(data.formFieldValueTypeId == FormFieldValueTypeEnum.String || data.formFieldValueTypeId == FormFieldValueTypeEnum.Textarea) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={<Switch checked={data.hasSelectCondition} onChange={handleChangeSwitch} name="hasSelectCondition" />}
                                            label="Has Select Condition"
                                        />
                                    </Grid2>
                                }


                                {(data.formFieldValueTypeId != FormFieldValueTypeEnum.Image && data.formFieldValueTypeId != FormFieldValueTypeEnum.FileDownload) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={<Switch checked={data.hasFormFieldKeyDependency} onChange={handleChangeSwitch} name="hasFormFieldKeyDependency" />}
                                            label="Has Form Field Dependency?"
                                        />
                                    </Grid2>
                                }



                                {(data.hasFormFieldKeyDependency === true && (sectionFormFieldKeys && sectionFormFieldKeys.length > 0)) &&
                                    <Grid2 size={{ xs: 12 }}>

                                        <DataTypeAutoComplete
                                            dataType={DataTypeEnum.FormFieldKeyDependency}
                                            label="Form Field Key Dependency"
                                            value={data.formFieldKeyDependencyId}
                                            name={"formFieldKeyDependencyId"}
                                            onChange={handleChange}
                                            parentId={_formTemplateSectionId}
                                            hasParent
                                            required />

                                    </Grid2>
                                }

                                {data.formFieldValueTypeId != FormFieldValueTypeEnum.Image &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={<Switch checked={data.hasFormFieldKeyLookupDependency} onChange={handleChangeSwitch} name="hasFormFieldKeyLookupDependency" />}
                                            label="Has Form Field Key Lookup Dependency?"
                                        />
                                    </Grid2>
                                }
                                {(data.hasFormFieldKeyLookupDependency === true) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormFieldKeyLookupMultipleAutoComplete
                                            required
                                            groupedBy={"formFieldKeyShortName"}
                                            parentId={_formTemplateSectionId}
                                            name={"formFieldKeyFormFieldKeyLookups"}
                                            items={data.formFieldKeyFormFieldKeyLookups}
                                            onChange={handleChange} />

                                    </Grid2>
                                }

                                <Grid2 size={{ xs: 12 }}>
                                    <FormControlLabel
                                        control={<Switch checked={data.hasFormFieldConditionDependency} onChange={handleChangeSwitch} name="hasFormFieldConditionDependency" />}
                                        label="Has Form Field Condition Dependency?"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>

                                    <DataTypeAutoComplete
                                        dataType={DataTypeEnum.FormFieldKeySpecialType}
                                        label="Form Field Key Special Default"
                                        value={data.formFieldKeySpecialTypeId}
                                        name={"formFieldKeySpecialTypeId"}
                                        onChange={handleChange} />
                                </Grid2>

                                {(data.hasFormFieldConditionDependency === true) &&
                                    <>
                                        <Grid2 size={{ xs: 12 }}>
                                            <EnumSelect
                                                enumObject={FormFieldKeyConditionTypeEnum}
                                                label="Condition Type"
                                                name="formFieldKeyConditionTypeId"
                                                value={Number(data.formFieldKeyConditionTypeId)}
                                                onChange={handleChange}
                                                required />
                                        </Grid2>

                                        <Grid2 size={{ xs: 12 }}>
                                            <StandardButton
                                                type="button"
                                                icon="check"
                                                onClick={() => { setData((_data: any) => ({ ..._data, formFieldKeyConditionDependencies: [..._data.formFieldKeyConditionDependencies, { formFieldKeyId: 0, formFieldKeyConditionId: 0, value: "" }] })) }} >
                                                Add Another Condition
                                            </StandardButton>
                                        </Grid2>

                                        {(data.formFieldKeyConditionDependencies && data.formFieldKeyConditionDependencies.length > 0) && data.formFieldKeyConditionDependencies.map((d: any, index: number) =>
                                            <>
                                                <Grid2 size={{ xs: 4 }}>

                                                    <DataTypeAutoComplete
                                                        dataType={DataTypeEnum.FormFieldKey}
                                                        label="Form Field Key Dependency"
                                                        value={data.formFieldKeyConditionDependencies[index].conditionFormFieldKeyId}
                                                        name={"conditionFormFieldKeyId"}
                                                        onChange={(event: any) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [..._data.formFieldKeyConditionDependencies]; formFieldKeyConditionDependencies[index].conditionFormFieldKeyId = event.target.value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}
                                                        parentId={_formTemplateSectionId}
                                                        hasParent
                                                        required />
                                                </Grid2>

                                                <Grid2 size={{ xs: 3 }}>

                                                    <SelectValidator
                                                        value={data.formFieldKeyConditionDependencies[index].formFieldKeyConditionId}
                                                        onChange={(event) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [..._data.formFieldKeyConditionDependencies]; formFieldKeyConditionDependencies[index].formFieldKeyConditionId = (event.target as any).value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}
                                                        required
                                                        label="Select Condition"
                                                        name="formFieldKeyConditionId"
                                                        fullWidth
                                                        validators={['required']}
                                                        errorMessages={['This field is required']}

                                                        InputLabelProps={{
                                                            shrink: !!data.formFieldKeyConditionDependencies[index].formFieldKeyConditionId,
                                                        }}>

                                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsTo.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsTo]}</MenuItem>
                                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsOrGreater.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsOrGreater]}</MenuItem>
                                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsOrLess.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsOrLess]}</MenuItem>
                                                        <MenuItem value={FormFieldKeyConditionEnum.Greater.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Greater]}</MenuItem>
                                                        <MenuItem value={FormFieldKeyConditionEnum.Less.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Less]}</MenuItem>
                                                        <MenuItem value={FormFieldKeyConditionEnum.Contains.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Contains]}</MenuItem>

                                                    </SelectValidator>
                                                </Grid2>
                                                <Grid2 size={{ xs: 3 }}>
                                                    <StandardTextValidator
                                                        label="Value"
                                                        value={data.formFieldKeyConditionDependencies[index].value}
                                                        name="value"
                                                        required
                                                        fullWidth
                                                        validators={['required']}
                                                        errorMessages={['This field is required']}
                                                        onChange={(event: any) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [..._data.formFieldKeyConditionDependencies]; formFieldKeyConditionDependencies[index].value = (event.target as any).value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}

                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 2 }}>
                                                    <StandardButton
                                                        type="button"
                                                        icon="clear"
                                                        loading={isLoading}
                                                        saving={saving}
                                                        onClick={() => { setData((_data: any) => { _data.formFieldKeyConditionDependencies.splice(index, 1); return { ..._data, formFieldKeyConditionDependencies: _data.formFieldKeyConditionDependencies }; }); }} >
                                                        Remove
                                                    </StandardButton>
                                                </Grid2>
                                            </>
                                        )}

                                    </>
                                }


                                <Grid2 size={{ xs: data.id == 0 ? 12 : 6 }}>
                                    <StandardButton
                                        type="submit"
                                        icon="check"
                                        loading={isLoading}
                                        saving={saving}
                                        disabled={isLoading}>
                                        {<>Save</>}
                                    </StandardButton>
                                </Grid2>

                                {(data.id > 0) &&
                                    <Grid2 size={{ xs: 6 }}>
                                        <StandardButton
                                            type="button"
                                            icon="clear"
                                            variant="error"
                                            loading={isLoading}
                                            saving={saving}
                                            disabled={isLoading}
                                            onClick={disable}>
                                            Disable
                                        </StandardButton>
                                    </Grid2>
                                }
                            </Grid2>

                        </ValidatorForm>
                    </TabPanel>
                    <TabPanel value="2" sx={{ p: 0.5, m: 0.5 }}>
                        {(tab === "2" && (shouldShowValues)) &&
                            <Outlet />
                        }
                    </TabPanel>
                </Grid2>

            </TabContext>

        </Drawer >
    );
}